import { FeatureLabelKey, PlanLimitCode, PlanLimitParams, PlanTierLabelKey } from '@seiri/shared/common'

/**
 * Slovak names of the plan tiers and features.
 *
 * The domain layer (`@seiri/shared/common`) and the API emit translation keys rather than locale
 * strings, so this is the only place plan wording exists. The key type is derived from FeatureKey
 * and PlanTier, so adding a feature or a tier is a compile error here until it is translated.
 */
export const PLAN_TRANSLATIONS_SK: Record<FeatureLabelKey | PlanTierLabelKey, string> = {
  // Tiers
  'plan.tier.start': 'Štart',
  'plan.tier.growth': 'Rast',
  'plan.tier.pro': 'Profi',
  'plan.tier.custom': 'Na mieru',

  // Features
  'plan.feature.customers': 'Počet klientov',
  'plan.feature.reservations_per_month': 'Rezervácií za mesiac',
  'plan.feature.employees': 'Zamestnanci',
  'plan.feature.places': 'Miestnosti',
  'plan.feature.activities': 'Činnosti',
  'plan.feature.event_blueprints': 'Opakujúce sa udalosti',
  'plan.feature.announcements': 'Oznamy',
  'plan.feature.data_export': 'Export dát',
  'plan.feature.web_elements': 'Web elementy',
  'plan.feature.sms_notifications': 'SMS notifikácie',
}

/**
 * Falls back to the key itself rather than an empty string: an untranslated key is visible in
 * the UI and easy to spot, where a blank label just looks broken.
 */
export const translatePlanKey = (key: string): string => (PLAN_TRANSLATIONS_SK as Record<string, string>)[key] ?? key

/** A 402 plan-limit refusal as a Slovak sentence. */
export function translatePlanLimit(code: PlanLimitCode, params: PlanLimitParams): string {
  const label = translatePlanKey(params.labelKey)
  const tier = translatePlanKey(`plan.tier.${params.tier}`)
  const upgrade = params.upgradeTo ? ` Prejdite na plán ${translatePlanKey(`plan.tier.${params.upgradeTo}`)}.` : ''

  switch (code) {
    case 'plan.limit.feature':
      return `Funkcia „${label}“ nie je dostupná v pláne ${tier}.${upgrade}`
    case 'plan.limit.quota':
      return (
        `Vyčerpali ste mesačný limit pre ${label.toLowerCase()} ` +
        `(${params.used}/${params.limit}) v pláne ${tier}.${upgrade}`
      )
    case 'plan.limit.count':
      return (
        `Dosiahli ste limit pre ${label.toLowerCase()} ` + `(${params.used}/${params.limit}) v pláne ${tier}.${upgrade}`
      )
  }
}
